import { FC, useMemo, useState } from "react";
// 组件
import { Table } from "antd";
import { ProTooltip } from "../../../antd";
import SuperTable from '../../../SuperTable';
// 方法
import { filterEmptyValue } from "../../../utils/utils";
import { accAdd } from '../../../utils/dataUtils';
// 常量
import { CHILDREN_SHOW_OPTION_KEY, DEFAULT_SHOW_LENGTH, MergeChildrenTableProps } from "../../interface";
import { columnsProps } from "../../../SuperTable/interface";
import { TABLE_COLUMN_EMPTY } from "../../../constants/constant";
import { calculateColumnsLength } from "../../utils";

/** 子表表格 */
const ChildrenTable: FC<MergeChildrenTableProps> = props => {
  const { 
    dataSource: outDataSource, columns: outColumns, defaultShowLength = DEFAULT_SHOW_LENGTH,
    childrenRowKey, subTotalKeys, subTotalText, ...restProps
  } = props;
  const [showAll, setShowAll] = useState<boolean>(false);
  /** 列配置 */
  const columns: columnsProps[] = useMemo(() => {
    if (defaultShowLength === false) return outColumns;
    if (Array.isArray(outDataSource) && (outDataSource.length > defaultShowLength)) {
      return outColumns.map((item, index) => {
        if (index === 0) {
          return {
            ...item,
            onCell: (record) => {
              if (record[CHILDREN_SHOW_OPTION_KEY] === true) {
                return {
                  colSpan: calculateColumnsLength(outColumns),
                };
              }
              return {};
            },
            render: (val, record, key) => {
              if (record[CHILDREN_SHOW_OPTION_KEY] === true) {
                if (index === 0) {
                  return (
                    <>
                      {showAll ? (
                        <div
                          className="main-sub-table-more"
                          onClick={() => setShowAll(false)}
                        >
                          点击收起
                        </div>
                      ) : (
                        <div
                          className="main-sub-table-more"
                          onClick={() => setShowAll(true)}
                        >
                          查看剩余{outDataSource.length - defaultShowLength - 1}条数据
                        </div>
                      )}
                    </>
                  )
                }
                return null;
              }
              if (item.render) return item.render(val, record, key);
              if (typeof val === 'string' || typeof val === 'number') {
                return (
                  <ProTooltip placement="topLeft" title={val}>
                    {val}
                  </ProTooltip>
                );
              }
              return filterEmptyValue(val);
            }
          };
        }
        return {
          ...item,
          onCell: (record) => {
            if (record[CHILDREN_SHOW_OPTION_KEY] === true) {
              return {
                colSpan: 0,
              };
            }
            return {};
          },
        };
      });
    }
    return outColumns;
  }, [outColumns, outDataSource, defaultShowLength, showAll]);
  /** 表格数据 */
  const dataSource = useMemo(() => {
    if (defaultShowLength === false) return outDataSource;
    if (Array.isArray(outDataSource) && (outDataSource.length > defaultShowLength)) {
      if (showAll) {
        return outDataSource;
      }
      return [...(outDataSource.slice(0, defaultShowLength)), ...(outDataSource.slice(-1))];
    }
    return outDataSource;
  }, [outDataSource, showAll, defaultShowLength]);
  /** 小计 */
  const handleSummary = () => {
    if (Array.isArray(subTotalKeys) && subTotalKeys.length && Array.isArray(outDataSource) && outDataSource.length) {
      const totalData: any = {};
      subTotalKeys.forEach(item => {
        totalData[item] = 0;
      });
      outDataSource.forEach(record => {
        subTotalKeys.forEach(item => {
          const totalValue: any = record[item];
          totalData[item] = accAdd(totalData[item], Number(totalValue) || 0);
        });
      });
      return (
        <Table.Summary.Row className="children-table-total">
          {
            outColumns.map((column, index) => {
              const { dataIndex, children, } = column;
              if (index === 0) {
                return <Table.Summary.Cell index={0} key={index}>{subTotalText}</Table.Summary.Cell>
              }
              if (Array.isArray(children) && children.length) {
                return children.map((item, itemIndex) => {
                  const childrenDataIndex = item.dataIndex;
                  if (typeof childrenDataIndex === 'string') {
                    return <Table.Summary.Cell index={Number(`${index + 100}${itemIndex}`)} key={`${index}-${itemIndex}`}>{filterEmptyValue(totalData[childrenDataIndex])}</Table.Summary.Cell>
                  }
                  return <Table.Summary.Cell index={Number(`${index + 100}${itemIndex}`)} key={`${index}-${itemIndex}`}>{TABLE_COLUMN_EMPTY}</Table.Summary.Cell>
                });
              }
              if (typeof dataIndex === 'string') {
                return <Table.Summary.Cell index={0} key={index}>{filterEmptyValue(totalData[dataIndex])}</Table.Summary.Cell>
              }
              return <Table.Summary.Cell index={index} key={index}>{TABLE_COLUMN_EMPTY}</Table.Summary.Cell>
            })
          }
        </Table.Summary.Row>
      );
    }
    return null;
  };
  /** 处理行标识 */
  const interChildrenRowKey = useMemo(() => {
    if (typeof childrenRowKey === 'function') {
      return (record: any) => {
        if (record[CHILDREN_SHOW_OPTION_KEY]) {
          return `${CHILDREN_SHOW_OPTION_KEY}-${Math.random()}`;
        }
        return childrenRowKey(record);
      };
    }
    return childrenRowKey;
  }, [childrenRowKey]);

  return (
    <SuperTable
      {...restProps}
      rowKey={interChildrenRowKey}
      columns={columns}
      dataSource={dataSource}
      showHeader={false}
      autoHeight={false}
      toolBar={false}
      pagination={false}
      operation={false}
      rowHoverable={false}
      sceneType='modal'
      hideScroll={true}
      isMergeChildrenTable={true}
      wrapperClassName="merge-children-table"
      contentClassName={Array.isArray(subTotalKeys) && subTotalKeys.length ? "merge-children-content-total" : "merge-children-content"}
      summary={handleSummary}
    />
  );
};

ChildrenTable.defaultProps = {
  subTotalText: '小计',
};

export default ChildrenTable;
